import React, { useState } from 'react';
import { Target, Plus, FileDown, Eye, Edit2, Trash2, Database } from 'lucide-react';
import { Typography } from 'antd';
import { Button as CrcButton } from '@crc-evans/ui-components';

import '../technical/HeatProfilePage.css';
import '../calibration/CalibrationPage.css';
import './MobilistPage.css';
import GenerateMobilistModal from './GenerateMobilistModal';
import MobilistDetailView from './MobilistDetailView';
import MobilistMasterDataView from './MobilistMasterDataView';
import { generateMobilistExcel } from '../../utils/excelGenerator';
import initialMasterData from '../../data/sapMasterList.json';

const { Title } = Typography;

const MobilistPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'detail' | 'master'
  const [currentMobilist, setCurrentMobilist] = useState(null);
  const [mobilists, setMobilists] = useState([]); // State to hold generated mobilists
  
  // Master data state so it persists across views
  const [masterData, setMasterData] = useState(
    initialMasterData
      .filter(item => !['DESCRIPTION', 'Info Only', 'Spares Packs', 'Daily Costs', 'Equipment'].includes(item['£ KEY']))
      .map((item, index) => ({ ...item, id: `md_${index}` }))
  );

  // Strip layout padding while on Mobilist pages
  React.useEffect(() => {
    const layoutContent = document.querySelector('.modern-layout-content');
    if (layoutContent) {
      layoutContent.classList.add('no-padding-layout');
    }
    return () => {
      if (layoutContent) {
        layoutContent.classList.remove('no-padding-layout');
      }
    };
  }, []);

  // Modal submit → close modal, open detail view with form data
  const handleGenerateSubmit = (values) => {
    setIsModalVisible(false);
    setCurrentMobilist(values);
    setViewMode('detail');
  };

  // Back / Cancel from detail view → return to list
  const handleBack = () => {
    setCurrentMobilist(null);
    setViewMode('list');
  };

  // Save from detail view → persist to state, return to list
  const handleSave = (data) => {
    setMobilists((prev) => [
      ...prev,
      {
        ...data,
        id: Date.now().toString(),
        totalCost: data.totalCost || 0, // calculated in detail view
        createdDate: new Date().toISOString(),
        updatedDate: new Date().toISOString(),
      },
    ]);
    setCurrentMobilist(null);
    setViewMode('list');
  };

  // Format currency
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(val || 0);
  };

  // Format date
  const formatDate = (isoString) => {
    const d = new Date(isoString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  // ── Detail view ──────────────────────────────────────────────
  if (viewMode === 'detail' && currentMobilist) {
    return (
      <MobilistDetailView
        data={currentMobilist}
        onBack={handleBack}
        onSave={handleSave}
      />
    );
  }

  // ── Master Data view ─────────────────────────────────────────
  if (viewMode === 'master') {
    return (
      <MobilistMasterDataView
        data={masterData}
        onUpdateData={setMasterData}
        onBack={() => setViewMode('list')}
      />
    );
  }

  // ── List view ────────────────────────────────────────────────
  return (
    <div className="heat-profile-content-wrapper admin-modern-page mobilist-full-width-page">
      <div className="heat-profile-card">

        {/* Header */}
        <div className="heat-profile-list-header">
          <div className="heat-profile-header-left">
            <div className="heat-profile-icon-wrap" style={{ backgroundColor: '#FEF3C7' }}>
              <Target size={22} color="#D97706" />
            </div>
            <div className="heat-profile-header-content">
              <Title level={4} className="heat-profile-list-title">Mobilist</Title>
              <p className="heat-profile-list-description">Manage and generate your mobilists.</p>
            </div>
          </div>
          <div className="heat-profile-header-actions calibration-modern-actions">
            <CrcButton
              label="Master Data"
              variant="secondary"
              icon={Database}
              onClick={() => setViewMode('master')}
              size="small"
              className="heat-profile-filter-btn"
            />
            <CrcButton
              label="Generate New Mobilist"
              variant="primary"
              icon={Plus}
              onClick={() => setIsModalVisible(true)}
              size="small"
              className="heat-profile-add-btn"
            />
          </div>
        </div>

        {/* Table */}
        <div className="heat-profile-table-wrap">
          <div className="mobilist-table-wrapper">
            <table className="custom-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>S.No</th>
                  <th>Project Name</th>
                  <th>Project Number</th>
                  <th>No. of Pipelines</th>
                  <th>Project Type</th>
                  <th>Total Cost</th>
                  <th>Created Date</th>
                  <th>Updated Date</th>
                  <th>Report</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mobilists.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-state">
                      <div className="ant-empty ant-empty-normal">
                        <div className="ant-empty-image">
                          <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                            <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                              <ellipse fill="#e2e8f0" cx="32" cy="33" rx="32" ry="7" />
                              <g fillRule="nonzero" stroke="#cbd5e1">
                                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" fill="#f8fafc" />
                                <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#f8fafc" />
                              </g>
                            </g>
                          </svg>
                        </div>
                        <div className="ant-empty-description">No data</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  mobilists.map((mob, index) => (
                    <tr key={mob.id}>
                      <td>{index + 1}</td>
                      <td style={{ fontWeight: 600 }}>{mob.projectName || '—'}</td>
                      <td>{mob.projectNumber || '—'}</td>
                      <td>{mob.numberOfPipelines || 1}</td>
                      <td>{mob.projectType || '—'}</td>
                      <td>{formatCurrency(mob.totalCost)}</td>
                      <td>{formatDate(mob.createdDate)}</td>
                      <td>{formatDate(mob.updatedDate)}</td>
                      <td>
                        <button 
                          className="action-btn" 
                          title="Download Excel"
                          onClick={() => generateMobilistExcel(mob)}
                        >
                          <FileDown size={18} />
                        </button>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button className="action-btn" title="View"><Eye size={16} /></button>
                          <button className="action-btn" title="Edit"><Edit2 size={16} /></button>
                          <button className="action-btn" title="Delete"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <GenerateMobilistModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleGenerateSubmit}
      />
    </div>
  );
};

export default MobilistPage;
